<template>
  <quota-menu-button
    :read-only="readOnly"
    v-model:is-open="isOpen"
    :hide-slider="hideSlider"
    :is-input-invalid="isInputInvalid"
    :is-alert-on="isAlertOn"
    :button-class="buttonClass"
    :tooltip-alert-class="tooltipAlertClass"
    @reset-value="initInput"
    @update:model-value="updateModelValue"
  >
    <template #editable-text>{{ inputPlaceholder }}</template>
    <template #resource-label> {{ EResourceLabel.MEMORY }}</template>
    <template #input>
      <runai-numeric-input
        class="q-mr-md"
        :class="{ 'quota-input': !hideSlider }"
        aid="memory-quota-input"
        ref="numeric-input"
        :autofocus="true"
        v-model.number="inputValue"
        :placeholder="EResourceState.Unlimited"
        input-class="wide-input"
        no-error-icon
        stack-label
        :read-only="readOnly"
      >
        <template v-if="isMemoryExceedsQuota || isMemoryUnderAllocatedNonPreemptible" #error>
          <div v-text="alertText" class="quota-exceeds-alert" :class="textAlertClass"></div>
        </template>
      </runai-numeric-input>
    </template>
    <template #units>
      <q-select
        aid="memory-unit-select"
        :options="memoryUnitOptions"
        :model-value="selectedUnit"
        @update:model-value="updateSelectedUnit"
      />
    </template>
    <template #slider>
      <div class="row q-my-xl">
        <div class="col q-pr-xl">
          <quota-slider
            v-model="inputValue"
            :max="departmentDeservedMemoryConverted"
            :inner-max="sliderInnerMax"
            :inner-min="sliderInnerMin"
            :is-warning-on="isMemoryExceedsQuota || isMemoryUnderAllocatedNonPreemptible"
            :is-limited="isLimitQuotaOverSubscriptionEnabled"
            inner-max-marker-text="Department available quota"
            :inner-min-marker-text="innerMinMarkerText"
            :hide-max="isDepartmentEntity"
          />
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="italic text-caption">
            For more information, see the
            <a :href="documentationLink" target="_blank">{{ documentationLinkText }}</a>
          </div>
        </div>
      </div></template
    >
    <template #alert-text>
      {{ alertText }}
    </template>
  </quota-menu-button>
</template>

<script lang="ts">
import { inject, type PropType, ref, toRef } from "vue";
import { defineComponent } from "vue";

// cmps
import { RunaiNumericInput } from "@/components/common/runai-numeric-input";

// models
import { EMemoryUnit, EQuotaEntity, EResourceLabel, EResourceState, memoryUnitOptions } from "@/models/resource.model";
//utils
import { resourceUtil } from "@/utils/resource.util";
//stores
import { QuotaSlider } from "@/components/org-unit/quota-management/quota-slider";
import {
  useQuotaInput,
  type useQuotaInputProperties,
} from "@/components/org-unit/quota-management/quota-inputs/quota-input-composable/use-quota-input.composeable";
import { QuotaMenuButton } from "@/components/org-unit/quota-management/quota-inputs/quota-menu-button";

export default defineComponent({
  components: {
    QuotaMenuButton,
    QuotaSlider,
    RunaiNumericInput,
  },
  emits: ["update:model-value", "close"],
  inject: {
    entity: {
      default: EQuotaEntity.project,
    },
  },
  props: {
    modelValue: {
      type: [Number, null] as PropType<number | null>,
      required: false,
    },
    readOnly: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    projectsDeservedMemory: {
      type: Number as PropType<number>,
      default: 0,
    },
    departmentDeservedMemory: {
      type: Number as PropType<number>,
      default: 0,
    },
    allocatedNonPreemptibleMemory: {
      type: Number as PropType<number>,
      default: 0,
    },
    isAlertOn: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    departmentDeservedMemoryIsUnlimited: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    hideSlider: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  data() {
    return {
      inputValue: 0 as number | null,
      inputPlaceholder: "" as string,
      selectedUnit: EMemoryUnit.MB as EMemoryUnit,
    };
  },
  setup(props) {
    const entity = ref(inject<EQuotaEntity>("entity"));
    if (!entity.value) {
      throw new Error("'entity' is not provided");
    }
    const {
      isProjectEntity,
      isOpen,
      isLimitQuotaOverSubscriptionEnabled,
      isDepartmentEnabled,
      documentationLink,
      documentationLinkText,
      innerMinMarkerText,
      buttonClass,
      textAlertClass,
      tooltipAlertClass,
    }: useQuotaInputProperties = useQuotaInput(entity.value, toRef(props, "isAlertOn"));
    return {
      isProjectEntity,
      isOpen,
      isLimitQuotaOverSubscriptionEnabled,
      isDepartmentEnabled,
      documentationLink,
      documentationLinkText,
      innerMinMarkerText,
      buttonClass,
      textAlertClass,
      tooltipAlertClass,
    };
  },
  created() {
    this.initInput();
  },
  computed: {
    EResourceLabel(): typeof EResourceLabel {
      return EResourceLabel;
    },
    EResourceState(): typeof EResourceState {
      return EResourceState;
    },
    memoryUnitOptions(): Array<string> {
      return memoryUnitOptions;
    },
    sliderInnerMin(): number {
      return this.isDepartmentEntity
        ? Math.round(this.fromMibToMemoryUnit(this.projectsDeservedMemory))
        : this.allocatedNonPreemptibleMemoryConverted;
    },
    sliderInnerMax(): number | undefined {
      if (this.departmentAvailableQuota < 0) return 0;
      return this.isDepartmentEntity ? undefined : this.departmentAvailableQuota;
    },
    isDepartmentEntity(): boolean {
      return this.entity === EQuotaEntity.department;
    },
    EQuotaEntity(): typeof EQuotaEntity {
      return EQuotaEntity;
    },
    departmentDeservedMemoryConverted(): number {
      return Math.round(this.fromMibToMemoryUnit(this.departmentDeservedMemory));
    },
    allocatedNonPreemptibleMemoryConverted(): number {
      return Math.round(this.fromMibToMemoryUnit(this.allocatedNonPreemptibleMemory));
    },
    innerProjectsDeservedMemory(): number {
      if (this.modelValue === null || this.modelValue === undefined) {
        return this.projectsDeservedMemory;
      }
      return this.projectsDeservedMemory - this.modelValue;
    },
    departmentAvailableQuota(): number {
      const quotaAdjustment = this.modelValue ?? 0;
      const availableQuota = this.departmentDeservedMemory - this.projectsDeservedMemory + quotaAdjustment;

      return Math.round(this.fromMibToMemoryUnit(availableQuota));
    },
    projectsDeservedMemoryWithCurrentInput(): number {
      if (this.inputValue !== null) {
        if (this.selectedUnit === EMemoryUnit.MB) {
          return this.innerProjectsDeservedMemory + resourceUtil.fromMbToMib(this.inputValue);
        } else {
          return this.innerProjectsDeservedMemory + resourceUtil.fromGbToMib(this.inputValue);
        }
      }
      return this.innerProjectsDeservedMemory;
    },
    isInputInvalid(): boolean {
      return (
        this.isLimitQuotaOverSubscriptionEnabled &&
        (this.isMemoryExceedsQuota || this.isMemoryUnderAllocatedNonPreemptible)
      );
    },
    isMemoryUnderAllocatedNonPreemptible(): boolean {
      if (this.isDepartmentEntity || !this.isDepartmentEnabled) return false;
      if (this.inputValue === null) return false;
      const inputValue =
        this.selectedUnit === EMemoryUnit.MB
          ? resourceUtil.fromMbToMib(this.inputValue)
          : resourceUtil.fromGbToMib(this.inputValue);
      return resourceUtil.isResourceUnderAllocatedNonPreemptible(inputValue, this.allocatedNonPreemptibleMemory);
    },
    isMemoryExceedsQuota(): boolean {
      if (!this.isDepartmentEnabled || this.departmentDeservedMemoryIsUnlimited) return false;
      if (this.isDepartmentEntity) {
        if (this.inputValue === null) return false;
        return this.inputValue < this.projectsDeservedMemory;
      }

      if (this.inputValue === null && !this.departmentDeservedMemoryIsUnlimited) return true;
      if (Math.trunc(this.departmentDeservedMemory) === Math.trunc(this.projectsDeservedMemoryWithCurrentInput)) {
        return false;
      }
      return resourceUtil.isResourceExceedsQuota(
        this.projectsDeservedMemoryWithCurrentInput,
        this.departmentDeservedMemory,
        this.departmentDeservedMemoryIsUnlimited,
      );
    },
    alertText(): string {
      if (this.isDepartmentEntity) {
        return `The department's quota is less than the quota assigned to its subordinate projects. To remain within the limit, enter a number above ${Math.round(
          this.fromMibToMemoryUnit(this.projectsDeservedMemory),
        )} ${this.selectedUnit}.`;
      }
      if (this.isMemoryExceedsQuota) {
        const remainQuota = this.departmentDeservedMemory - (this.projectsDeservedMemory - (this.modelValue || 0));
        return remainQuota < 0
          ? "The project's quota exceeds the available department quota"
          : `The project's quota exceeds the available department quota. To remain within the quota, enter a number up to ${Math.round(
              this.fromMibToMemoryUnit(remainQuota),
            )} ${this.selectedUnit}.`;
      }
      if (this.isMemoryUnderAllocatedNonPreemptible) {
        return `The project's quota is less than the usage by non-preemptive workloads. To remain above the usage, enter a number above ${Math.round(
          this.fromMibToMemoryUnit(this.allocatedNonPreemptibleMemory),
        )} ${this.selectedUnit}`;
      }
      return "";
    },
  },
  methods: {
    initInput(): void {
      if (this.modelValue !== null && this.modelValue !== undefined) {
        const convertedValue = resourceUtil.convertMemoryValue(this.modelValue);
        this.inputValue = parseFloat(convertedValue.value.toFixed(2));
        this.selectedUnit = convertedValue.selectedUnit;
        this.inputPlaceholder = `${this.inputValue} ${convertedValue.selectedUnit}`;
      } else {
        this.inputPlaceholder = EResourceState.Unlimited;
        this.inputValue = null;
      }
    },
    updateModelValue(): void {
      if (this.isInputInvalid) return;
      if (this.inputValue === null) {
        this.inputPlaceholder = EResourceState.Unlimited;
        this.$emit("update:model-value", null);
      } else if (this.selectedUnit === EMemoryUnit.MB) {
        this.$emit("update:model-value", resourceUtil.fromMbToMib(this.inputValue));
        this.inputPlaceholder = `${this.inputValue} ${this.selectedUnit}`;
      } else if (this.selectedUnit === EMemoryUnit.GB) {
        this.$emit("update:model-value", resourceUtil.fromGbToMib(this.inputValue));
        this.inputPlaceholder = `${this.inputValue} ${this.selectedUnit}`;
      }
    },
    updateSelectedUnit(unit: EMemoryUnit): void {
      this.selectedUnit = unit;
    },
    fromMibToMemoryUnit(mib: number): number {
      return resourceUtil.fromMibToMemoryUnit(this.selectedUnit, mib);
    },
  },
});
</script>
